import React, { useMemo, useState } from 'react';
import { Button, DatePicker, Space, Typography, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import ExcelExportOverlay from '../../components/common/ExcelExportOverlay';
import orderWiseRevenueExportService from '../../services/orderWiseRevenueExportService';
import {
  ORDER_WISE_REVENUE_EXPORT_COLUMNS,
  mapOrderWiseRevenueExportRow,
} from './commons/orderWiseRevenue.helpers';

const { RangePicker } = DatePicker;
const { Title, Paragraph } = Typography;

function defaultMtdRange() {
  const end = dayjs();
  return [end.startOf('month'), end];
}

export default function OrderWiseRevenuePage() {
  const [range, setRange] = useState(defaultMtdRange);
  const [exportOpen, setExportOpen] = useState(false);

  const startDate = range?.[0]?.format('YYYY-MM-DD');
  const endDate = range?.[1]?.format('YYYY-MM-DD');

  const exportFilename = useMemo(() => {
    if (!startDate || !endDate) return 'order_wise_revenue';
    return `order_wise_revenue_${startDate}_${endDate}`;
  }, [startDate, endDate]);

  const handleExport = () => {
    if (!startDate || !endDate) {
      message.error('Select a start and end date');
      return;
    }
    if (range[0].isAfter(range[1], 'day')) {
      message.error('Start date must be on or before end date');
      return;
    }
    setExportOpen(true);
  };

  return (
    <DashboardPageWrapper>
      <div className="p-6 max-w-3xl">
        <Title level={3} style={{ marginBottom: 8 }}>
          Order-wise Revenue
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 24 }}>
          Download fulfilled orders with Altcase platform margin, RM net, central uplift (net +
          gross), and MIS bucket — same eligibility and formulas as Sales MIS — for spreadsheet
          verification.
        </Paragraph>

        <Space wrap size="middle" align="start">
          <div>
            <div style={{ marginBottom: 6, fontWeight: 500 }}>Date range (IST)</div>
            <RangePicker
              value={range}
              onChange={(value) => setRange(value)}
              allowClear={false}
              format="YYYY-MM-DD"
            />
          </div>
          <div style={{ paddingTop: 28 }}>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              disabled={exportOpen || !startDate || !endDate}
              onClick={handleExport}
            >
              Export Excel
            </Button>
          </div>
        </Space>
      </div>

      <ExcelExportOverlay
        open={exportOpen}
        exportConfig={{
          filename: exportFilename,
          sheetName: 'Order Revenue',
          columns: ORDER_WISE_REVENUE_EXPORT_COLUMNS,
          mapRow: mapOrderWiseRevenueExportRow,
          filterParams: { startDate, endDate },
          pageSize: 100,
          fetchPage: async (filterParams, page, pageSize) => {
            const data = await orderWiseRevenueExportService.list({
              startDate: filterParams.startDate,
              endDate: filterParams.endDate,
              page,
              limit: pageSize,
            });
            return {
              records: data?.records ?? [],
              total: data?.total ?? data?.pagination?.total ?? 0,
            };
          },
        }}
        onClose={() => setExportOpen(false)}
      />
    </DashboardPageWrapper>
  );
}
